import { storage } from '../utils/storage';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { firebaseConfig } from '../constants/firebaseConfig';
import { reconcilePremium } from './premiumReconcile';
import type { StoredBillingAuth } from '../types/billing';

/**
 * Stripe/Firestore premium sync — the single writer of premium_status
 * (PREM-001).
 *
 * Reads the server-written `users/{uid}/billing/subscription` doc over the
 * Firestore REST API — deliberately NOT the Firebase Web SDK, which is
 * initialised only from UI surfaces (see utils/firebase.ts) and whose auth
 * persistence is not exercised inside the MV3 service worker. The UI pushes
 * `{ uid, idToken, refreshToken }` into chrome.storage (billingAuth.ts); this
 * module refreshes the short-lived ID token itself via
 * securetoken.googleapis.com, so the hourly alarm and SW startups work even
 * when no UI has been opened for days. Both hosts already have
 * host_permissions entries.
 *
 * Failure semantics (shared reconcile body, PREM-002):
 * missing auth, expired-and-unrefreshable tokens, HTTP or network errors all
 * THROW, which reconcilePremium treats as "unknown — keep the stored status".
 * Only an authoritative answer (doc read OK, or a 404 = never subscribed) can
 * start the 7-day grace countdown.
 */

const FETCH_TIMEOUT_MS = 5000;
// Refresh the ID token when it has less than this long to live.
const TOKEN_EXPIRY_SLACK_MS = 60 * 1000;
// Subscription statuses treated as paid. past_due is intentionally excluded:
// the shared 7-day grace period already keeps premium through Stripe's own
// dunning window without a second, overlapping grace mechanism.
const PAID_STATUSES = new Set(['active', 'trialing']);

// De-duplicate concurrent syncs: several triggers can fire at once (e.g. SW
// startup + UI_READY); a single in-flight fetch is shared between them.
let inFlight: Promise<boolean> | null = null;

/**
 * Query Firestore for the Stripe subscription state and reconcile local
 * premium_status.
 * @returns the effective premium state after reconciliation (true = paid).
 */
export async function syncPremiumFromFirestore(reason: string): Promise<boolean> {
  if (inFlight) return inFlight;
  inFlight = reconcilePremium(
    'syncPremiumFromFirestore',
    'Stripe billing',
    reason,
    fetchPaidFromFirestore
  ).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function fetchPaidFromFirestore(): Promise<boolean> {
  const auth = (await storage.get(STORAGE_KEYS.BILLING_AUTH)) as StoredBillingAuth | null;
  if (!auth?.uid || !auth.idToken) {
    // Signed out (or the UI has never pushed credentials). Throwing routes
    // this through the never-downgrade path: a paying user who signs out is
    // not stripped of premium, exactly like an unreachable billing backend.
    throw new Error('No Firebase billing credentials in storage (user not signed in)');
  }
  const idToken = await ensureFreshIdToken(auth);
  const status = await fetchSubscriptionStatus(auth.uid, idToken);
  return status !== null && PAID_STATUSES.has(status);
}

async function ensureFreshIdToken(auth: StoredBillingAuth): Promise<string> {
  if (auth.expiresAt && auth.expiresAt - Date.now() > TOKEN_EXPIRY_SLACK_MS) {
    return auth.idToken;
  }
  if (!auth.refreshToken) {
    throw new Error('Firebase ID token expired and no refresh token stored');
  }

  const response = await fetchWithTimeout(
    `https://securetoken.googleapis.com/v1/token?key=${firebaseConfig.apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: auth.refreshToken,
      }).toString(),
    }
  );
  if (!response.ok) {
    throw new Error(`Firebase token refresh failed (HTTP ${response.status})`);
  }
  const data = (await response.json()) as {
    id_token?: string;
    refresh_token?: string;
    expires_in?: string;
  };
  if (!data.id_token) {
    throw new Error('Firebase token refresh returned no id_token');
  }

  const updated: StoredBillingAuth = {
    ...auth,
    idToken: data.id_token,
    refreshToken: data.refresh_token ?? auth.refreshToken,
    expiresAt: Date.now() + Number(data.expires_in ?? 3600) * 1000,
  };
  await storage.set(STORAGE_KEYS.BILLING_AUTH, updated);
  return updated.idToken;
}

/**
 * @returns the subscription `status` string, or null when the billing doc
 *   does not exist (an authoritative "never subscribed"). Throws on anything
 *   that is not an authoritative answer.
 */
async function fetchSubscriptionStatus(uid: string, idToken: string): Promise<string | null> {
  const url =
    `https://firestore.googleapis.com/v1/projects/${firebaseConfig.projectId}` +
    `/databases/(default)/documents/users/${uid}/billing/subscription`;
  const response = await fetchWithTimeout(url, {
    headers: { Authorization: `Bearer ${idToken}` },
  });

  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Firestore billing read failed (HTTP ${response.status})`);
  }

  const doc = (await response.json()) as {
    fields?: { status?: { stringValue?: string } };
  };
  const status = doc.fields?.status?.stringValue;
  return typeof status === 'string' ? status : null;
}

async function fetchWithTimeout(
  url: string,
  init?: Parameters<typeof fetch>[1]
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}
